import { inject, Injectable } from '@angular/core';
import { StepStatusEnum } from '@lib/enums/step-status.enum';
import { Civilite, civiliteOptions } from '@lib/models/civilite.model';
import { DocumentType, GedDocumentType, TypeRelationAutresGQL } from '@lib/models/generated/graphql';
import { ModeLogement, modeLogementOptionsSelect } from '@lib/models/mode-logement.model';
import { typeRegimeMatrimonialMariageOptions, typeRegimeMatrimonialPacsOptions } from '@lib/models/regime-matrimonial.model';
import { typeRelationFamilleOptions } from '@lib/models/type-relation-famille.model';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { map, Observable, of, shareReplay, switchMap } from 'rxjs';
import {
  CategorieSocioProfessionnellesGQL,
  DocumentCategoriesGQL,
  DocumentTypesGQL,
  EtatCni,
  EtatDer,
  EtatQpi,
  EtatRecueil,
  Pays,
  PaysGQL,
  RegimeActivitesGQL,
  SecteurActivitesGQL,
  SignatureState,
  TypeAvantageMatrimoniauxGQL,
  TypePpeMotifsGQL,
  TypePpeRelationsGQL,
  TypeSituationProfessionnellesGQL,
  TypeStatutProfessionnelsGQL,
} from '../../public-api';
import { niveauEtudeOptions } from './../models/niveau-etude.model';

@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  private readonly paysGQL = inject(PaysGQL);
  private readonly typeAvantageMatrimoniauxGQL = inject(TypeAvantageMatrimoniauxGQL);
  private readonly typePpeMotifsGQL = inject(TypePpeMotifsGQL);
  private readonly typePpeRelationsGQL = inject(TypePpeRelationsGQL);
  private readonly regimeActivitesGQL = inject(RegimeActivitesGQL);
  private readonly categorieSocioProfessionnellesGQL = inject(CategorieSocioProfessionnellesGQL);
  private readonly secteurActivitesGQL = inject(SecteurActivitesGQL);
  private readonly typeSituationProfessionnellesGQL = inject(TypeSituationProfessionnellesGQL);
  private readonly typeStatutProfessionnelsGQL = inject(TypeStatutProfessionnelsGQL);
  private readonly documentTypesGQL = inject(DocumentTypesGQL);
  private readonly documentCategoriesGQL = inject(DocumentCategoriesGQL);
  private readonly typeAutreRelationGQL = inject(TypeRelationAutresGQL);

  public readonly typeRelationFamilleOptions = typeRelationFamilleOptions;

  public readonly origineContactOptions: OuiSelectOption<string>[] = [
    { label: 'Ami', value: 'AMI' },
    { label: 'Apporteur d’affaires', value: 'APP_AFF' },
    { label: 'Arrangement entre conseillers', value: 'ARR_CONS' },
    { label: 'Autre partenaire professionnel', value: 'AUT_PART' },
    { label: 'Avocat', value: 'AVO' },
    { label: 'Club d’entrepreneurs', value: 'CLUB' },
    { label: 'Événement privé', value: 'EVT_PRIVE' },
    { label: 'Événement professionnel', value: 'EVT_PRO' },
    { label: 'Expert-comptable', value: 'EXP_CPT' },
    { label: 'Famille', value: 'FAM' },
    { label: 'Investisseur', value: 'INV' },
    { label: 'Notaire', value: 'NOT' },
    { label: 'Prospection (autre)', value: 'PROS_AUT' },
    { label: 'Prospection téléphonique', value: 'PROS_TEL' },
    { label: 'Rattachement suite à une résiliation', value: 'RAT_RESI' },
    { label: 'Recommandation', value: 'RECO' },
    { label: 'Rencontre', value: 'RENC' },
    { label: 'Réseaux sociaux', value: 'REZO' },
    { label: 'Site internet (MonSite Pro)', value: 'SITE' },
    { label: 'Sport', value: 'SPO' },
    { label: 'Autre', value: 'AUTRE' },
  ];

  public readonly origineContactFilterOptions: OuiSelectOption<string>[] = [
    { label: 'Ami', value: 'AMI' },
    { label: 'Apporteur d’affaires', value: 'APP_AFF' },
    { label: 'Autre partenaire professionnel', value: 'AUT_PART' },
    { label: 'Avocat', value: 'AVO' },
    { label: 'Club d’entrepreneurs', value: 'CLUB' },
    { label: 'Événement privé', value: 'EVT_PRIVE' },
    { label: 'Événement professionnel', value: 'EVT_PRO' },
    { label: 'Expert-comptable', value: 'EXP_CPT' },
    { label: 'Famille', value: 'FAM' },
    { label: 'Investisseur', value: 'INV' },
    { label: 'Notaire', value: 'NOT' },
    { label: 'Rattachement suite à une résiliation', value: 'RAT_RESI' },
    { label: 'Recommandation', value: 'RECO' },
    { label: 'Rencontre', value: 'RENC' },
    { label: 'Autre', value: 'AUTRE' },
  ];

  public readonly civiliteOptions: OuiButtonToggleOption<Civilite>[] = civiliteOptions;
  public readonly modeLogementOptionsSelect: OuiSelectOption<ModeLogement>[] = modeLogementOptionsSelect;

  public readonly niveauEtudeOptions = niveauEtudeOptions;

  public readonly typeRegimeMatrimonialMariageOptions: OuiSelectOption<string>[] = typeRegimeMatrimonialMariageOptions;

  public readonly typeRegimeMatrimonialPacsOptions: OuiSelectOption<string>[] = typeRegimeMatrimonialPacsOptions;

  public readonly signatureStateOptions: OuiSelectOption<SignatureState>[] = [
    { label: 'Annulé', value: SignatureState.Cancelled },
    { label: 'En cours', value: SignatureState.Pending },
    { label: 'En erreur', value: SignatureState.Failed },
    { label: 'Expiré', value: SignatureState.Expired },
    { label: 'Signé', value: SignatureState.Completed },
  ];

  public readonly yesNoOptions: OuiButtonToggleOption<boolean>[] = [
    { label: 'Oui', value: true },
    { label: 'Non', value: false },
  ];

  // Pays
  private paysCache$?: Observable<Pays[]>;
  private paysOptions$?: Observable<OuiSelectOption<string>[] | null>;
  private nationalitesOptions$?: Observable<OuiSelectOption<string>[] | null>;
  private fetchPays(): Observable<Pays[]> {
    if (!this.paysCache$) {
      this.paysCache$ = this.paysGQL.fetch().pipe(
        map(result => result.data!.pays as Pays[]),
        shareReplay(1)
      );
    }
    return this.paysCache$;
  }
  getPays(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.paysOptions$) {
      this.paysOptions$ = this.fetchPays().pipe(
        map(result => this.toSelectOptions(result)),
        map(options => {
          if (!options) return null;
          const res = options.sort((a, b) => {
            if (a.value === 'FRA') return -1;
            if (b.value === 'FRA') return 1;
            return a.label.localeCompare(b.label);
          });
          res[0].divider = true;
          return res;
        }),
        shareReplay(1)
      );
    }
    return this.paysOptions$;
  }

  getNationalites(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.nationalitesOptions$) {
      this.nationalitesOptions$ = this.fetchPays().pipe(
        map(pays => {
          return pays
            .filter(p => p.nationalite)
            .map(
              p =>
                ({
                  label: p.nationalite,
                  value: p.nationaliteCode,
                }) as OuiSelectOption<string>
            );
        }),
        map(options => {
          if (!options) return null;
          const res = options.sort((a, b) => {
            if (a.value === 'FRA') return -1;
            if (b.value === 'FRA') return 1;
            return a.label.localeCompare(b.label);
          });
          res[0].divider = true;
          return res;
        }),
        shareReplay(1)
      );
    }
    return this.nationalitesOptions$;
  }

  //  Type ppe relation
  private typePpeRelationsCache$?: Observable<OuiSelectOption<string>[] | null>;
  getTypePpeRelations(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.typePpeRelationsCache$) {
      this.typePpeRelationsCache$ = this.typePpeRelationsGQL.fetch().pipe(
        switchMap(result => of(this.toSelectOptions(result.data!.typePpeRelations))),
        shareReplay(1)
      );
    }
    return this.typePpeRelationsCache$;
  }

  // Type autre relation
  private typeAutreRelationsCache$?: Observable<OuiSelectOption<any>[]>;
  getTypeAutreRelations(): Observable<OuiSelectOption<any>[]> {
    if (!this.typeAutreRelationsCache$) {
      this.typeAutreRelationsCache$ = this.typeAutreRelationGQL.fetch().pipe(
        map(result =>
          [...result.data!.typeRelationAutres]
            .filter(s => s.code != 'NR')
            .sort((a, b) => a.libelle.localeCompare(b.libelle))
            .map(item => ({ label: item.libelle.charAt(0).toUpperCase() + item.libelle.slice(1), value: item.code }))
        ),
        shareReplay(1)
      );
    }
    return this.typeAutreRelationsCache$;
  }

  //  Type ppe motif
  private typePpeMotifsCache$?: Observable<OuiSelectOption<string>[] | null>;
  getTypePpeMotifs(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.typePpeMotifsCache$) {
      this.typePpeMotifsCache$ = this.typePpeMotifsGQL.fetch().pipe(
        switchMap(result => {
          let options = this.toSelectOptions(result.data!.typePpeMotifs);
          options = options.filter(o => o.value !== 'NR');
          return of(options);
        }),
        shareReplay(1)
      );
    }
    return this.typePpeMotifsCache$;
  }

  //  Type avantage matrimoniaux
  private typeAvantageMatrimoniauxCache$?: Observable<OuiSelectOption<string>[] | null>;
  public getTypeAvantageMatrimoniaux(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.typeAvantageMatrimoniauxCache$) {
      this.typeAvantageMatrimoniauxCache$ = this.typeAvantageMatrimoniauxGQL.fetch().pipe(
        switchMap(result => {
          const options = this.toSelectOptions(result.data!.typeAvantageMatrimoniaux.filter(s => s.code !== 'DON_DER')).map(option => ({
            ...option,
            label: option.label.replace("''", "'"),
          }));
          return of(options);
        }),
        shareReplay(1)
      );
    }
    return this.typeAvantageMatrimoniauxCache$;
  }

  //  Regime activite
  private regimeActivitesCache$?: Observable<OuiSelectOption<number>[] | null>;
  getRegimeActivites(): Observable<OuiSelectOption<number>[] | null> {
    if (!this.regimeActivitesCache$) {
      this.regimeActivitesCache$ = this.regimeActivitesGQL.fetch().pipe(
        switchMap(result => of(this.toSelectOptions<number>(result.data!.regimeActivites))),
        shareReplay(1)
      );
    }
    return this.regimeActivitesCache$;
  }

  //  Categorie socio professionnelle
  private categorieSocioProfessionnellesCache$?: Observable<OuiSelectOption<number>[] | null>;
  getCategorieSocioProfessionnelles(): Observable<OuiSelectOption<number>[] | null> {
    if (!this.categorieSocioProfessionnellesCache$) {
      this.categorieSocioProfessionnellesCache$ = this.categorieSocioProfessionnellesGQL.fetch().pipe(
        switchMap(result => of(this.toSelectOptions<number>(result.data!.categorieSocioProfessionnelles))),
        shareReplay(1)
      );
    }
    return this.categorieSocioProfessionnellesCache$;
  }

  //  Secteur activite
  private secteurActivitesCache$?: Observable<OuiSelectOption<number>[] | null>;
  getSecteurActivites(): Observable<OuiSelectOption<number>[] | null> {
    if (!this.secteurActivitesCache$) {
      this.secteurActivitesCache$ = this.secteurActivitesGQL.fetch().pipe(
        switchMap(result => of(this.toSelectOptions<number>(result.data!.secteurActivites))),
        shareReplay(1)
      );
    }
    return this.secteurActivitesCache$;
  }

  //  Type situation profesionnelle
  private typeSituationProfessionnellesCache$?: Observable<OuiSelectOption<number>[] | null>;
  getTypeSituationProfessionnelles(): Observable<OuiSelectOption<number>[] | null> {
    if (!this.typeSituationProfessionnellesCache$) {
      this.typeSituationProfessionnellesCache$ = this.typeSituationProfessionnellesGQL.fetch().pipe(
        switchMap(result => of(this.toSelectOptions<number>(result.data!.typeSituationProfessionnelles))),
        shareReplay(1)
      );
    }
    return this.typeSituationProfessionnellesCache$;
  }

  //  Document type
  private documentTypesCache$?: Observable<DocumentType[]>;
  public getDocumentTypes(): Observable<DocumentType[]> {
    if (!this.documentTypesCache$) {
      this.documentTypesCache$ = this.documentTypesGQL.fetch().pipe(
        map(result => result.data!.documentTypes as unknown as DocumentType[]),
        shareReplay(1)
      );
    }
    return this.documentTypesCache$;
  }

  private filteredDocumentTypesOptionCache$?: Observable<DocumentType[] | null>;
  getFilteredDocumentTypes(): Observable<DocumentType[] | null> {
    if (!this.filteredDocumentTypesOptionCache$) {
      const docToRemove = [GedDocumentType.CniHeberg.toString(), GedDocumentType.CniMere.toString(), GedDocumentType.CniPere.toString()];
      this.filteredDocumentTypesOptionCache$ = this.getDocumentTypes().pipe(
        map(result => result.filter(d => !docToRemove.includes(d.gedTypeCode.toString()))),
        shareReplay(1)
      );
    }
    return this.filteredDocumentTypesOptionCache$;
  }

  private documentTypeCategoryRecordsCache$?: Observable<Record<string, string>>;
  getDocumentTypeCategoryRecords(): Observable<Record<string, string>> {
    if (!this.documentTypeCategoryRecordsCache$) {
      this.documentTypeCategoryRecordsCache$ = this.getDocumentTypes().pipe(
        map(result => {
          const acc: Record<string, string> = {};
          for (const dt of result) {
            acc[dt.gedTypeCode] = dt.documentCategory.gedCategoryCode;
          }
          return acc;
        }),
        shareReplay(1)
      );
    }
    return this.documentTypeCategoryRecordsCache$;
  }

  //  Document category
  private documentCategoriesCache$?: Observable<OuiSelectOption<string>[] | null>;
  getDocumentCategories(): Observable<OuiSelectOption<string>[] | null> {
    if (!this.documentCategoriesCache$) {
      this.documentCategoriesCache$ = this.documentCategoriesGQL.fetch().pipe(
        switchMap(result => of(result.data!.documentCategories.map(item => ({ label: item.label, value: item.gedCategoryCode, data: item.id })))),
        shareReplay(1)
      );
    }
    return this.documentCategoriesCache$;
  }

  private toSelectOptions<T>(items: { code: T; libelle: string }[]): OuiSelectOption<T>[] {
    return items.map(item => ({
      label: item.libelle,
      value: item.code,
      data: item,
    }));
  }

  public static mapDERState(status: EtatDer): StepStatusEnum {
    switch (status) {
      case EtatDer.ASigner:
        return StepStatusEnum.ToSign;
      case EtatDer.EnSignature:
      case EtatDer.EnSignatureApresSigne:
        return StepStatusEnum.Signing;
      case EtatDer.Signe:
        return StepStatusEnum.Signed;
      case EtatDer.ACompleter:
        return StepStatusEnum.ToComplete;
      default:
        return StepStatusEnum.ToComplete;
    }
  }
  public static mapRecueilState(status: EtatRecueil): StepStatusEnum {
    switch (status) {
      case EtatRecueil.ASigner:
        return StepStatusEnum.ToSign;
      case EtatRecueil.EnSignatureApresExpiration:
      case EtatRecueil.EnSignatureApresSigne:
      case EtatRecueil.EnSignaturePremierFois:
        return StepStatusEnum.Signing;
      case EtatRecueil.Signe:
        return StepStatusEnum.Signed;
      case EtatRecueil.Expire:
        return StepStatusEnum.Expired;
      case EtatRecueil.ACompleter:
        return StepStatusEnum.ToComplete;
      default:
        return StepStatusEnum.ToComplete;
    }
  }

  public static mapQpiState(status: EtatQpi): StepStatusEnum {
    switch (status) {
      case EtatQpi.ACompleter:
        return StepStatusEnum.ToComplete;
      case EtatQpi.ASigner:
        return StepStatusEnum.ToSign;
      case EtatQpi.EnSignatureApresExpiration:
      case EtatQpi.EnSignatureApresSigne:
      case EtatQpi.EnSignaturePremierFois:
        return StepStatusEnum.Signing;
      case EtatQpi.Signe:
      case EtatQpi.SigneNouveauASigner:
      case EtatQpi.SigneNouveauACompleter:
        return StepStatusEnum.Signed;
      case EtatQpi.Expire:
        return StepStatusEnum.Expired;
      default:
        return StepStatusEnum.ToComplete;
    }
  }

  public static mapCNIState(status: EtatCni): StepStatusEnum {
    switch (status) {
      case EtatCni.Ajoute:
        return StepStatusEnum.Added;
      case EtatCni.Manquant:
        return StepStatusEnum.ToAdd;
      case EtatCni.Expire:
        return StepStatusEnum.Expired;
      default:
        return StepStatusEnum.ToAdd;
    }
  }

  readonly tauxMarginalOptions: OuiSelectOption<number>[] = [
    { value: 0, label: '0' },
    { value: 11, label: '11' },
    { value: 30, label: '30' },
    { value: 41, label: '41' },
    { value: 45, label: '45' },
  ];
}
